import React from "react";
import { FaSave, FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import InlineAlert from "../../../UI/DesignStandards/Notifications/InlineAlert";
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  SoftButton,
} from "../../../UI/DesignStandards/Buttons";

const OrganizationLinksSection = ({
  links,
  showAddLink,
  editingLink,
  newLink,
  formError,
  onSetShowAddLink,
  onSetEditingLink,
  onLinkChange,
  onSaveLink,
  deletingLinkId,
  deletingLinkInProgressId,
  onStartConfirmDelete,
  onCancelConfirmDelete,
  onConfirmDelete,
}) => {
  return (
    <div className="card-board p-4 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-3 sm:space-y-0">
        <h2 className="heading-display text-xl">Organization Links</h2>
        <SecondaryButton
          onClick={() => onSetShowAddLink(true)}
          className="!px-3 !py-2 text-sm"
        >
          <FaPlus className="w-4 h-4" />
          <span>Add Link</span>
        </SecondaryButton>
      </div>

      {showAddLink && !editingLink && (
        <div className="mb-6 p-4 card-inset">
          <h3 className="text-lg font-medium mb-4">Add New Link</h3>
          {formError ? (
            <div className="mb-3">
              <InlineAlert
                type="error"
                title="Please fix the following"
                message={formError}
              />
            </div>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSaveLink();
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  Type *
                </label>
                <select
                  value={newLink.type}
                  onChange={(e) => onLinkChange("type", e.target.value)}
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                >
                  <option value="">Select a type</option>
                  <option value="website">Website</option>
                  <option value="facebook">Facebook</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube">YouTube</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="tiktok">TikTok</option>
                  <option value="usbc">USBC</option>
                  <option value="donation">Donation</option>
                  <option value="registration">Registration</option>
                  <option value="calendar">Calendar</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-charcoal mb-2">
                  URL *
                </label>
                <input
                  type="text"
                  value={newLink.url}
                  onChange={(e) => onLinkChange("url", e.target.value)}
                  placeholder="https://example.com or www.example.com"
                  className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <PrimaryButton type="submit">
                <FaSave className="w-4 h-4" />
                <span>Add Link</span>
              </PrimaryButton>
              <SecondaryButton
                type="button"
                className="!px-3 !py-2 text-sm"
                onClick={() => {
                  onSetShowAddLink(false);
                  onLinkChange("resetNew", null);
                }}
              >
                Cancel
              </SecondaryButton>
            </div>
          </form>
        </div>
      )}

      {links.length === 0 ? (
        <div className="text-center py-8 text-brand-muted">
          <svg
            className="text-4xl mx-auto mb-4 text-brand-mist"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          <p>No links added yet.</p>
          <p className="text-sm mt-2">Add social media links, websites, etc.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <div
              key={link.id}
              className="flex flex-col border border-brand-charcoal/10 rounded-lg hover:bg-brand-cream/50 transition-colors p-4 mb-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 text-brand-muted mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 00-5.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                      <span className="font-medium text-brand-charcoal capitalize">
                        {link.type}
                      </span>
                    </div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-gold-ink hover:text-brand-charcoal underline font-medium hover:no-underline transition-all duration-200 text-sm sm:text-base break-all"
                    >
                      {link.url}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <SoftButton
                    color="cream"
                    size="sm"
                    onClick={() => onSetEditingLink(link)}
                    title={`Edit ${link.type} link`}
                    iconLeft={<FaEdit className="h-3.5 w-3.5" />}
                  >
                    Edit
                  </SoftButton>
                  {deletingLinkId === link.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-600 font-medium">
                        Delete?
                      </span>
                      <SoftButton
                        color="red"
                        size="xs"
                        type="button"
                        onClick={() => onConfirmDelete(link.id)}
                        disabled={deletingLinkInProgressId === link.id}
                        aria-label={`Confirm delete ${link.type} link`}
                      >
                        {deletingLinkInProgressId === link.id
                          ? "Deleting..."
                          : "Yes"}
                      </SoftButton>
                      <SoftButton
                        color="cream"
                        size="xs"
                        type="button"
                        onClick={() => onCancelConfirmDelete()}
                        aria-label={`Cancel delete ${link.type} link`}
                      >
                        No
                      </SoftButton>
                    </div>
                  ) : (
                    <SoftButton
                      color="red"
                      size="sm"
                      onClick={() => onStartConfirmDelete(link.id)}
                      title={`Delete ${link.type} link`}
                      aria-label={`Delete ${link.type} link`}
                      iconLeft={<FaTrash className="h-3.5 w-3.5" />}
                    >
                      Delete
                    </SoftButton>
                  )}
                </div>
              </div>
              {editingLink && editingLink.id === link.id && (
                <div className="mt-4 p-4 card-inset">
                  <h3 className="text-lg font-medium mb-4">Edit Link</h3>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSaveLink();
                    }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          Type *
                        </label>
                        <select
                          value={editingLink.type}
                          onChange={(e) => onLinkChange("type", e.target.value)}
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        >
                          <option value="">Select a type</option>
                          <option value="website">Website</option>
                          <option value="facebook">Facebook</option>
                          <option value="twitter">Twitter/X</option>
                          <option value="instagram">Instagram</option>
                          <option value="youtube">YouTube</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="tiktok">TikTok</option>
                          <option value="usbc">USBC</option>
                          <option value="donation">Donation</option>
                          <option value="registration">Registration</option>
                          <option value="calendar">Calendar</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-charcoal mb-2">
                          URL *
                        </label>
                        <input
                          type="text"
                          value={editingLink.url}
                          onChange={(e) => onLinkChange("url", e.target.value)}
                          placeholder="https://example.com or www.example.com"
                          className="w-full px-3 py-2 border border-brand-charcoal/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                      <PrimaryButton type="submit">
                        <FaSave className="w-4 h-4" />
                        <span>Update Link</span>
                      </PrimaryButton>
                      <SecondaryButton
                        type="button"
                        className="!px-3 !py-2 text-sm"
                        onClick={() => onSetEditingLink(null)}
                      >
                        Cancel
                      </SecondaryButton>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrganizationLinksSection;
